import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

import { SERVICE_OPTIONS, PROJECT_SIZE_OPTIONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLASSES =
  "bg-zinc-950 border border-zinc-800 rounded-none h-12 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-1 focus-visible:ring-[#FF5A00] focus-visible:border-[#FF5A00] focus-visible:ring-offset-0";
const LABEL_CLASSES =
  "font-heading text-xs tracking-[0.25em] uppercase text-zinc-400";

export default function QuoteForm({ compact = false }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_type: "",
    project_size: "",
    location: "",
    message: "",
  });

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received. Our team will reach out within 1 business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service_type: "",
        project_size: "",
        location: "",
        message: "",
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          ? `Submission failed: ${err.response.data.detail}`
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="quote-form"
      onSubmit={submit}
      className={`bg-zinc-900 border border-zinc-800 p-8 lg:p-10 ${
        compact ? "" : "shadow-[0_30px_60px_-30px_rgba(255,90,0,0.3)]"
      }`}
    >
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="eyebrow">Project Intake</div>
          <h3 className="font-display text-3xl sm:text-4xl tracking-wider uppercase text-zinc-50 mt-3">
            Request a Quote
          </h3>
        </div>
        <span className="hidden sm:block font-heading text-xs tracking-[0.3em] uppercase text-zinc-600">
          24 hr reply
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="q-name" className={LABEL_CLASSES}>
            Full Name *
          </Label>
          <Input
            id="q-name"
            data-testid="quote-name"
            value={form.name}
            onChange={update("name")}
            placeholder="Jane Doe"
            className={INPUT_CLASSES}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="q-company" className={LABEL_CLASSES}>
            Company
          </Label>
          <Input
            id="q-company"
            data-testid="quote-company"
            value={form.company}
            onChange={update("company")}
            placeholder="Acme Industrial"
            className={INPUT_CLASSES}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="q-email" className={LABEL_CLASSES}>
            Email *
          </Label>
          <Input
            id="q-email"
            data-testid="quote-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@company.com"
            className={INPUT_CLASSES}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="q-phone" className={LABEL_CLASSES}>
            Phone *
          </Label>
          <Input
            id="q-phone"
            data-testid="quote-phone"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(555) 010-2025"
            className={INPUT_CLASSES}
            required
          />
        </div>

        <div className="space-y-2">
          <Label className={LABEL_CLASSES}>Service Needed *</Label>
          <Select value={form.service_type} onValueChange={update("service_type")}>
            <SelectTrigger
              data-testid="quote-service"
              className={`${INPUT_CLASSES} flex items-center`}
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border border-zinc-800 rounded-none text-zinc-100">
              {SERVICE_OPTIONS.map((s) => (
                <SelectItem
                  key={s.value}
                  value={s.value}
                  className="font-body focus:bg-zinc-800 focus:text-[#FF5A00]"
                >
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className={LABEL_CLASSES}>Project Size</Label>
          <Select value={form.project_size} onValueChange={update("project_size")}>
            <SelectTrigger
              data-testid="quote-size"
              className={`${INPUT_CLASSES} flex items-center`}
            >
              <SelectValue placeholder="Approximate sq ft" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border border-zinc-800 rounded-none text-zinc-100">
              {PROJECT_SIZE_OPTIONS.map((s) => (
                <SelectItem
                  key={s.value}
                  value={s.value}
                  className="font-body focus:bg-zinc-800 focus:text-[#FF5A00]"
                >
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="q-location" className={LABEL_CLASSES}>
            Project Location
          </Label>
          <Input
            id="q-location"
            data-testid="quote-location"
            value={form.location}
            onChange={update("location")}
            placeholder="City, State"
            className={INPUT_CLASSES}
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="q-message" className={LABEL_CLASSES}>
            Project Details *
          </Label>
          <Textarea
            id="q-message"
            data-testid="quote-message"
            value={form.message}
            onChange={update("message")}
            placeholder="Tell us about your facility, square footage, timeline, special requirements…"
            className={`${INPUT_CLASSES} min-h-[140px] py-3`}
            required
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="quote-submit"
        disabled={submitting}
        className="btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            Send Request <ArrowUpRight className="w-4 h-4" />
          </>
        )}
      </button>
      <p className="text-xs text-zinc-500 mt-4 font-body">
        By submitting, you agree to be contacted about your project. We never
        share your information.
      </p>
    </form>
  );
}
